import dayjs from 'dayjs';
import { Fragment, useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Box, Divider, LoadingOverlay, ScrollArea, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

import { useQuery } from '@tanstack/react-query';

import { selectPersonId } from '@/store/user-process/user-selectors.ts';

import { AppRoutes } from '@/router/routes.ts';

import { AppContext } from '@/contexts/AppContext/AppContextProvider';

import { personalPlacesApi } from '@/shared/api/personalPlaces';
import useAppStore from '@/shared/store/app.store';
import useUserStore from '@/shared/store/user.store.ts';
import { IObject } from '@/shared/types/bookingObjects.types';
import { TPersonalPlaceActive } from '@/shared/types/personalPlaces.types';
import { BottomSheet } from '@/shared/uikit/components/bottomSheet';
import { ModalSheet } from '@/shared/uikit/components/modalSheet';
import { IconicRow } from '@/shared/uikit/components/rows/IconicRow';
import { LayoutRow } from '@/shared/uikit/layouts/LayoutRow';

import { PersonalPlaceDetail } from './PersonalPlaceDetail';
import { PersonalPlacesList } from './PersonalPlacesList';
import { useAppSelector } from '@/hooks';

export const PersonalPlaces = () => {
	const navigate = useNavigate();
	const isMobile = useAppStore((state) => state.isMobile);
	const setPersonalPlaces = useUserStore((state) => state.setPersonalPlaces);
	const personId = useAppSelector(selectPersonId);
	const [selectedPlace, setSelectedPlace] = useState<IObject & TPersonalPlaceActive>();

	const [
		openedPersonalPlacesList,
		{ close: closePersonalPlacesList, open: openPersonalPlacesList },
	] = useDisclosure(false);

	const [openedPlaceDetail, { close: closePlaceDetail, open: openPlaceDetail }] =
		useDisclosure(false);

	const { data: getPersonalPlaces, isLoading: isLoadingPersonalPlaces } = useQuery({
		queryKey: ['personalPlaces'],
		queryFn: personalPlacesApi.getPersonalPlaces,
	});

	const { data: getPersonalAllPlaces, isLoading: isLoadingPersonalAllPlaces } = useQuery({
		queryKey: ['AllPersonalPlaces'],
		queryFn: personalPlacesApi.getAllPersonalPlaces,
		staleTime: 60 * 60 * 1000,
	});

	const currentPersonPlaces = useMemo(() => {
		if (!getPersonalAllPlaces) return [];

		const personal = getPersonalAllPlaces.filter((place) => place.owner_person_id === personId);

		if (personal.length) {
			setPersonalPlaces(personal);
		}

		return personal;
	}, [getPersonalAllPlaces, personId]);

	const handleShowMore = () => {
		if (isMobile) {
			openPersonalPlacesList();
		} else {
			navigate(AppRoutes.PersonalPlaces);
		}
	};

	const handleOpenPlaceDetail = (place: IObject, state: TPersonalPlaceActive) => {
		setSelectedPlace({ ...place, ...state });
		openPlaceDetail();
	};

	if (!currentPersonPlaces.length) return;

	return (
		<>
			<Box pos="relative">
				<LoadingOverlay
					visible={!currentPersonPlaces && isLoadingPersonalPlaces}
					zIndex={1000}
					overlayProps={{ radius: 2, blur: 2 }}
				/>
				<LayoutRow
					type="once"
					title={{
						title: {
							children: 'Личные места',
							size: 'h4',
							fw: 800,
						},
						button: {
							children: `подробнее`,
							onClick: handleShowMore,
						},
					}}
				>
					{currentPersonPlaces.map((place) => {
						const activeState = getPersonalPlaces?.find(
							(release) => release.booking_object_id === place.id,
						);

						// const placeDescription = <PlaceLocation placeId={place.id} /> ?? '';

						return (
							<Fragment key={place.id}>
								<IconicRow
									mainText={place?.name?.replace(/\s\|\s[\d\D]+/gm, '')}
									dimmedText={place.description}
									onClick={() => {
										handleOpenPlaceDetail(
											// { ...place, description: placeDescription as unknown as string },
											place,
											{
												start: dayjs(activeState?.start).toDate() ?? null,
												finish: dayjs(activeState?.finish).toDate() ?? null,
												release_id: activeState?.id ?? null,
												active: !!activeState,
											},
										);
									}}
									{...(activeState && {
										rightContentSlot: (
											<Text c="dimmed" size="sm" fw={500}>
												Свободный доступ
											</Text>
										),
									})}
									clickable
								/>
								{currentPersonPlaces[currentPersonPlaces.length - 1] !== place && (
									<Divider my={8} color="#6670851A" />
								)}
							</Fragment>
						);
					})}
				</LayoutRow>
			</Box>

			{openedPersonalPlacesList && (
				<PersonalPlacesList open={openedPersonalPlacesList} close={closePersonalPlacesList} />
			)}

			{isMobile && selectedPlace && (
				<BottomSheet
					opened={openedPlaceDetail}
					onClose={closePlaceDetail}
					title={selectedPlace?.name}
					withBackButton={false}
					isFullScreen
				>
					<PersonalPlaceDetail object={selectedPlace} />
				</BottomSheet>
			)}

			{!isMobile && selectedPlace && (
				<ModalSheet
					opened={openedPlaceDetail}
					onClose={closePlaceDetail}
					title={selectedPlace?.name}
					size="lg"
					centered
				>
					<PersonalPlaceDetail object={selectedPlace} />
				</ModalSheet>
			)}
		</>
	);
};
